import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SuggestingInput, { suggest } from 'react/elements/SuggestingInput';
import MaterialIcon from 'react/elements/MaterialIcon';
import { addGroceryItemKind, fetchGroceryItem, setGroceryItemKindOf } from 'redux/actions/groceryItems';
import { selectGroceryItemCatalog } from '../../../redux/food/selectors';
import useOffline, { onlineOnly, onlineOnlyField, useSaveWhenOnline } from '../../hooks/useOffline';

const SUGGESTION_COUNT = 8;

/**
 * The item editor's Kinds: what the item is a kind of, or the kinds it has. Trix is a kind of Cereal,
 * and Cereal has Trix and Cheerios. Kinds go one level deep, so a kind shows only its generic, which
 * can be let go of, while any other item lists its kinds with a box to add one. An item with neither
 * also offers to be a kind of something.
 *
 * The names offered are the budget's items, the food store's catalog: for a kind, those that are
 * kinds of nothing yet; for a generic, those that are not kinds themselves. Each change is folded into
 * the catalog as it is answered, so what is offered follows it.
 *
 * `onSaved` is given the item's details after each change, `onError` a message when one is refused.
 * Given `onOpenItem`, which takes an item's id and name, each name here opens its item.
 *
 * Every change is online-only, so offline the removes are disabled and the boxes read-only, titled
 * "Needs a connection"; a name left in a box offline is saved once back online.
 */
export default function ItemKinds({ details, onSaved, onError, onOpenItem = null }) {
	const item = details.item;
	const kinds = details.kinds || [];
	const kindOf = details.kind_of || null;
	const dispatch = useDispatch();
	const items = useSelector((state) => selectGroceryItemCatalog(state.food.entities));
	const [newKind, setNewKind] = useState('');
	const [newGeneric, setNewGeneric] = useState('');
	const offline = useOffline();
	const whenOnline = useSaveWhenOnline(offline);

	const save = (request) => request.then(onSaved, (error) => onError(
		error && error.responseJSON && error.responseJSON.error ? error.responseJSON.error : 'That did not save. Try again.'
	));

	const generics = new Set(items.map((each) => each.kind_of_id).filter(Boolean));
	const named = (each) => each.id !== item.id;
	const kindCandidates = items.filter((each) => named(each) && !generics.has(each.id) && each.kind_of_id === null).map((each) => each.name);
	const genericCandidates = items.filter((each) => named(each) && each.kind_of_id === null).map((each) => each.name);

	const addKind = (typed = newKind) => {
		const name = typed.trim();
		setNewKind('');
		if(name) {
			save(dispatch(addGroceryItemKind(item.id, name)));
		}
	};
	const makeKindOf = (typed = newGeneric) => {
		const name = typed.trim();
		setNewGeneric('');
		if(name) {
			save(dispatch(setGroceryItemKindOf(item.id, name)));
		}
	};

	const nameOf = (other) => (onOpenItem ?
		<a
			href='#'
			className='grocery_item_kinds__open'
			onClick={(event) => {
				event.preventDefault();
				onOpenItem(other.id, other.name);
			}}
		>
			{other.name}
		</a> :
		other.name);

	if(kindOf) {
		return (
			<section className='meal_idea_panel__section grocery_item_kinds'>
				<h6>Kind of</h6>
				<div className='grocery_item_details__tag-list'>
					<span className='grocery_item_details__tag'>
						{nameOf(kindOf)}
						<button
							type='button'
							className='btn-flat grocery_item_details__tag-remove'
							aria-label={`${item.name} is not a kind of ${kindOf.name}`}
							title={`Not a kind of ${kindOf.name}`}
							{...onlineOnly(offline)}
							onClick={() => save(dispatch(setGroceryItemKindOf(item.id, '')))}
						>
							<MaterialIcon>close</MaterialIcon>
						</button>
					</span>
				</div>
			</section>
		);
	}

	return (
		<section className='meal_idea_panel__section grocery_item_kinds'>
			<h6>Kinds</h6>
			<div className='grocery_item_details__tag-list'>
				{kinds.map((kind) =>
				<span key={kind.id} className='grocery_item_details__tag'>
					{nameOf(kind)}
					<button
						type='button'
						className='btn-flat grocery_item_details__tag-remove'
						aria-label={`${kind.name} is not a kind of ${item.name}`}
						title={`Not a kind of ${item.name}`}
						{...onlineOnly(offline)}
						onClick={() => save(dispatch(setGroceryItemKindOf(kind.id, '')).then(() => fetchGroceryItem(item.id)))}
					>
						<MaterialIcon>close</MaterialIcon>
					</button>
				</span>
				)}
				<div className='input-field grocery_item_details__tag-input'>
					<SuggestingInput
						aria-label='Add a kind'
						placeholder={kinds.length ? 'Add a kind' : `A kind of ${item.name}`}
						{...onlineOnlyField(offline)}
						value={newKind}
						onChange={setNewKind}
						suggestions={suggest(kindCandidates, kinds.map((kind) => kind.name), newKind, SUGGESTION_COUNT)}
						onSubmit={addKind}
						menuId={`grocery-item-kinds-${item.id}`}
						menuClassName='grocery_item_details__suggestions'
						onBlur={() => whenOnline('kind', () => addKind())}
					/>
				</div>
			</div>
			{kinds.length === 0 &&
			<div className='grocery_item_kinds__of'>
				<span className='grocery_item_details__label'>Or a kind of</span>
				<div className='input-field grocery_item_details__tag-input'>
					<SuggestingInput
						aria-label='Make it a kind of'
						placeholder='Cereal, bread, pasta...'
						{...onlineOnlyField(offline)}
						value={newGeneric}
						onChange={setNewGeneric}
						suggestions={suggest(genericCandidates, [], newGeneric, SUGGESTION_COUNT)}
						onSubmit={makeKindOf}
						menuId={`grocery-item-kind-of-${item.id}`}
						menuClassName='grocery_item_details__suggestions'
						onBlur={() => whenOnline('kind-of', () => makeKindOf())}
					/>
				</div>
			</div>
			}
		</section>
	);
}
